/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { dropTask, timeout, Task } from 'ember-concurrency';
import { action } from '@ember/object';
import { FlyMouseEvent } from '@customerio/fly-components/types/fly-components/events';
import { AVAILABLE_LANGUAGES, Language } from 'cio/utils/languages';
import AlertsService from 'cio/services/alerts';

const MAX_TRANSLATIONS = 50;
const TOOLTIP_TRACK_DELAY = 1000;

interface LanguageOption {
  label: string;
  value: string;
  lowerCaseLabel: string;
  lowerCaseValue: string;
}

interface LanguageSelectorArgs {
  selectedLanguages: string[];
  showAttributeTooltip?: boolean;
  onCancel: (evt?: FlyMouseEvent<HTMLElement>) => unknown;
  onAdd: Task<unknown, [string[]]>;
  onAttributeInfoIconHover?: (evt: FlyMouseEvent<HTMLSpanElement>) => unknown;
  onSettingsLinkClicked?: (evt: FlyMouseEvent<HTMLAnchorElement>) => unknown;
  fieldsetClassNames?: string;
  selectedListClassNames?: string;
  footerClassNames?: string;
  availableLanguages?: Language[];
  onPersistTemporaryProgress?: (languages: string[]) => unknown;
  initialLanguagesToAdd?: string[];
  hasReadOnlyPermission: boolean;
}

export default class LanguageSelector extends Component<LanguageSelectorArgs> {
  @service alerts!: AlertsService;

  @tracked languagesToAdd: string[] = [];
  @tracked search = '';

  constructor(owner: unknown, args: LanguageSelectorArgs) {
    super(owner, args);
    if (args.initialLanguagesToAdd) {
      this.languagesToAdd = args.initialLanguagesToAdd;
    }
  }

  get filteredLanguageOptions() {
    let lowerCaseSearch = this.search.toLowerCase();

    return this.languageOptions.filter(({ lowerCaseLabel, lowerCaseValue }) => {
      let matchesCode: boolean = lowerCaseValue.includes(lowerCaseSearch);
      let matchesName: boolean = lowerCaseLabel.includes(lowerCaseSearch);

      return matchesCode || matchesName;
    });
  }

  get noLanguagesSelected() {
    return this.languagesToAdd.length === 0;
  }

  get addDisabled() {
    return this.args.hasReadOnlyPermission || this.noLanguagesSelected || this.args.onAdd.isRunning;
  }

  get availableLanguages(): Language[] {
    return this.args.availableLanguages ?? AVAILABLE_LANGUAGES;
  }

  get languageOptions(): LanguageOption[] {
    return this.availableLanguages.reduce((acc: LanguageOption[], language) => {
      let languageCodeLowerCase = language.code.toLowerCase();
      if (!this.args.selectedLanguages.includes(languageCodeLowerCase)) {
        acc = [
          ...acc,
          {
            label: language.name,
            value: language.code,
            lowerCaseLabel: language.name.toLowerCase(),
            lowerCaseValue: languageCodeLowerCase,
          },
        ];
      }

      return acc;
    }, []);
  }

  @action
  setLanguagesToAdd(languages: LanguageOption[]) {
    this.languagesToAdd = languages.map((language) => language.value);
  }

  @action
  toggleLanguage(checked: boolean, value: string) {
    if (checked) {
      this.languagesToAdd.addObject(value);
    } else {
      this.languagesToAdd.removeObject(value);
    }
    this.args.onPersistTemporaryProgress?.(this.languagesToAdd);
  }

  @action
  deselectAll() {
    this.languagesToAdd = [];
  }

  @action
  onCheck(checked: boolean, { event }: { event: Event }) {
    let { value } = event.target as HTMLInputElement;
    this.toggleLanguage(checked, value);
  }

  @action
  trackLanguageSettingsClick(evt: FlyMouseEvent<HTMLAnchorElement>) {
    this.args.onSettingsLinkClicked?.(evt);
  }

  @action
  handleTooltipMouseLeave() {
    this.handleTooltipMouseEnter.cancelAll();
  }

  handleTooltipMouseEnter = dropTask(async (evt: FlyMouseEvent<HTMLSpanElement>) => {
    try {
      await timeout(TOOLTIP_TRACK_DELAY);

      this.args.onAttributeInfoIconHover?.(evt);
    } catch {}
  });

  onAdd = dropTask(async (evt: SubmitEvent) => {
    evt.preventDefault();

    let totalLanguages = this.args.selectedLanguages.length + this.languagesToAdd.length;
    if (totalLanguages > MAX_TRANSLATIONS) {
      this.alerts.warning('translation.creation.limit', `Only up to ${MAX_TRANSLATIONS} languages are supported.`);
      return;
    }

    this.args.onAdd.perform(this.languagesToAdd);
  });
}
